import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { SectionHeader } from "@/components/site/SectionHeader";
import { Reveal } from "@/components/site/Reveal";
import {
  Phone,
  AssetDetailScreen,
  PortfolioScreen,
  MonitoringScreen,
  WatchlistScreen,
  PresaleScreen,
} from "@/components/site/PhoneMockup";
import { fadeUp, viewport } from "@/lib/motion";
import { CheckCircle } from "@phosphor-icons/react";

const Row = ({ reverse, label, title, desc, points, children }) => (
  <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      className={reverse ? "md:order-2" : ""}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-400">
        {label}
      </span>
      <h3 className="mt-4 font-display text-2xl font-bold text-white md:text-3xl">
        {title}
      </h3>
      <p className="mt-4 text-base leading-relaxed text-white/55">{desc}</p>
      <ul className="mt-6 space-y-3">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-white/70">
            <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-teal-400" />
            {p}
          </li>
        ))}
      </ul>
    </motion.div>
    <div className={`flex justify-center ${reverse ? "md:order-1" : ""}`}>
      {children}
    </div>
  </div>
);

const FloatPhone = ({ children, testid }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  return (
    <motion.div ref={ref} style={{ y }} className="relative scale-90 sm:scale-100">
      <Phone testid={testid}>{children}</Phone>
    </motion.div>
  );
};

export const Showcase = () => (
  <section
    id="showcase"
    className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-4 py-24 sm:px-6 md:py-32"
    data-testid="showcase-section"
  >
    <SectionHeader
      label="Product"
      title="A closer look at the platform"
      desc="Polished, consistent product experiences designed for clarity — from AI analysis to monitoring and portfolio tracking."
      align="center"
    />

    <div className="mt-20 space-y-28">
      <Row
        label="AI Analysis & Asset Detail"
        title="Understand any asset at a glance"
        desc="See an AI Score, Risk Score, recommendation and time-based outlooks alongside a detailed breakdown of each crypto asset."
        points={[
          "Composite AI Score from multiple market factors",
          "Transparent risk breakdown — volatility, liquidity, momentum",
          "Time-based outlooks for 24h, 7d and 30d horizons",
        ]}
      >
        <FloatPhone testid="showcase-detail">
          <AssetDetailScreen />
        </FloatPhone>
      </Row>

      <Row
        reverse
        label="Monitoring & Presale"
        title="Stay informed, automatically"
        desc="Monitor selected assets and receive intelligent insights, and evaluate presales with AI-powered intelligence and external market context."
        points={[
          "Intelligent notifications for monitored assets",
          "Presale reviews using external information where available",
          "Insights framed as analysis — never guarantees",
        ]}
      >
        <div className="flex items-center justify-center gap-4">
          <FloatPhone testid="showcase-monitoring">
            <MonitoringScreen />
          </FloatPhone>
          <div className="hidden lg:block">
            <Phone className="scale-[0.82] opacity-90" glow={false} testid="showcase-presale">
              <PresaleScreen />
            </Phone>
          </div>
        </div>
      </Row>

      <Row
        label="Portfolio & Watchlist"
        title="Keep everything in one place"
        desc="Track your holdings and follow the assets that matter to you with clear, consistent information across the whole platform."
        points={[
          "Track holdings and portfolio information",
          "Curated watchlist for opportunities you care about",
          "Consistent, professional UI across every screen",
        ]}
      >
        <div className="flex items-center justify-center gap-4">
          <FloatPhone testid="showcase-portfolio">
            <PortfolioScreen />
          </FloatPhone>
          <div className="hidden lg:block">
            <Phone className="scale-[0.82] opacity-90" glow={false} testid="showcase-watchlist">
              <WatchlistScreen />
            </Phone>
          </div>
        </div>
      </Row>
    </div>
  </section>
);
